/**
 * Every URL of the influencer side of the new campaign flow (brd_* tables), in
 * one place — the web twin of the influencer app's Home / Branded Partnerships /
 * campaign details / My Campaigns. Screens navigate with these builders, never
 * hand-written paths.
 *
 *   /influencer/home                    Home: Branded Partnerships + Affiliate Campaigns
 *   /influencer/campaigns               Branded Partnerships (the full feed)   ?q= · ?platform=instagram,youtube · ?type=Paid,Barter · ?invited=1 · ?relevant=1 · ?near=1
 *   /influencer/campaigns/:campaignId   A campaign: details + Apply
 *   /influencer/my-campaigns            My Campaigns                            ?tab=completed
 *   /influencer/chats                   Chat: Campaign Messages                 ?tab=direct → Direct Messages
 *   /influencer/chats/:proposalId       One conversation (campaign or direct, by its brd_campaign_proposals id)
 *   /influencer/notifications           Notifications (the bell)
 *   /influencer/profile[/…]             Profile and its screens: personal-info, company-details,
 *                                       addresses, bank-accounts, notifications, subscription-history,
 *                                       security[/deactivate|/delete]
 *
 * routes/route.tsx mounts the flow as the /influencer/* catch-all inside the
 * /influencer/ route, so sign-in, the Zap-only guard for foreign creators and
 * the phone footer apply as on every other influencer page, and every other
 * /influencer URL stays the older page's. The legacy pages keep their URLs
 * (/influencer/campaign/*, /influencer/mycampaigns …): old emails and links
 * point at them. The flow's first URLs (/influencer/brd/…) redirect to the
 * same page here.
 */

export const INF_BASE = '/influencer';

/** The flow's top-level pages (InfluencerFlowRoutes) — everything else under /influencer is an older page. */
const FLOW_SECTIONS = ['home', 'campaigns', 'my-campaigns', 'chats', 'notifications', 'profile'];

/** Whether a path is in the new influencer flow (App.tsx frames these pages before the flow's code loads). */
export const isInfluencerFlowPath = (pathname: string) => {
  // Route matching ignores case, so this does too.
  const [, root, section] = pathname.toLowerCase().split('/');
  return root === 'influencer' && FLOW_SECTIONS.includes(section);
};

/**
 * The pages that show the influencer site's phone footer (MainInfluencer reads
 * this) — a campaign's page has its own Apply bar there instead, as in the app.
 */
export const INF_FOOTER_PATHS = [`${INF_BASE}/home`, `${INF_BASE}/campaigns`, `${INF_BASE}/my-campaigns`, `${INF_BASE}/profile`];
export const showsPhoneFooter = (pathname: string) => INF_FOOTER_PATHS.includes(pathname.replace(/\/+$/, ''));

export type FeedPlatform = 'instagram' | 'youtube';
export type FeedType = 'Paid' | 'Barter';

/** The Branded Partnerships feed's filters (the app's CampaignFeedFilters). */
export interface CampaignFeedFilters {
  platforms: FeedPlatform[];
  types: FeedType[];
  /** Only campaigns a brand invited me to. */
  invited: boolean;
  /** Only campaigns in my categories. */
  relevant: boolean;
  /** Only campaigns open to all of India or targeting my state / city. */
  nearMe: boolean;
}

export const NO_FEED_FILTERS: CampaignFeedFilters = { platforms: [], types: [], invited: false, relevant: false, nearMe: false };

export const countFeedFilters = (f: CampaignFeedFilters) =>
  f.platforms.length + f.types.length + (f.invited ? 1 : 0) + (f.relevant ? 1 : 0) + (f.nearMe ? 1 : 0);

const PLATFORMS: FeedPlatform[] = ['instagram', 'youtube'];
const TYPES: FeedType[] = ['Paid', 'Barter'];

/** The feed's filters + search from its query string (unknown values are dropped). */
export function parseFeedQuery(search: URLSearchParams): { filters: CampaignFeedFilters; q: string } {
  const csv = (key: string) => (search.get(key) || '').split(',').map((v) => v.trim()).filter(Boolean);
  const platforms = PLATFORMS.filter((p) => csv('platform').map((v) => v.toLowerCase()).includes(p));
  const types = TYPES.filter((t) => csv('type').map((v) => v.toLowerCase()).includes(t.toLowerCase()));
  return {
    filters: {
      platforms,
      types,
      invited: search.get('invited') === '1',
      relevant: search.get('relevant') === '1',
      nearMe: search.get('near') === '1',
    },
    q: (search.get('q') || '').slice(0, 100),
  };
}

const query = (params: Record<string, string | number | null | undefined>) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 0)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `?${qs}` : '';
};

export const infPaths = {
  home: () => `${INF_BASE}/home`,
  /** Branded Partnerships; `filters` / `q` open it already filtered (e.g. the invite banner). */
  campaigns: (opts?: { filters?: Partial<CampaignFeedFilters>; q?: string }) => {
    const f = opts?.filters;
    return `${INF_BASE}/campaigns${query({
      q: opts?.q?.trim() || undefined,
      platform: f?.platforms?.length ? f.platforms.join(',') : undefined,
      type: f?.types?.length ? f.types.join(',') : undefined,
      invited: f?.invited ? 1 : undefined,
      relevant: f?.relevant ? 1 : undefined,
      near: f?.nearMe ? 1 : undefined,
    })}`;
  },
  campaign: (campaignId: number | string) => `${INF_BASE}/campaigns/${campaignId}`,
  myCampaigns: (tab?: 'applied' | 'completed') => `${INF_BASE}/my-campaigns${tab === 'completed' ? '?tab=completed' : ''}`,
  chats: (tab?: 'campaign' | 'direct') => `${INF_BASE}/chats${tab === 'direct' ? '?tab=direct' : ''}`,
  chat: (proposalId: number | string) => `${INF_BASE}/chats/${proposalId}`,
  notifications: () => `${INF_BASE}/notifications`,
  profile: () => `${INF_BASE}/profile`,
  personalInfo: () => `${INF_BASE}/profile/personal-info`,
  companyDetailsPage: () => `${INF_BASE}/profile/company-details`,
  addresses: () => `${INF_BASE}/profile/addresses`,
  bankAccounts: () => `${INF_BASE}/profile/bank-accounts`,
  notificationSettings: () => `${INF_BASE}/profile/notifications`,
  subscriptionHistory: () => `${INF_BASE}/profile/subscription-history`,
  security: () => `${INF_BASE}/profile/security`,
  deactivateAccount: () => `${INF_BASE}/profile/security/deactivate`,
  deleteAccount: () => `${INF_BASE}/profile/security/delete`,

  // Pages of the existing influencer site the new flow links to.
  zap: () => '/influencer/zap',
  socialConnect: () => '/influencer/settings/socialconnect',
  community: () => '/influencer/settings/community',
  subscription: () => '/influencer/settings/subscription',
  // The chat sheets' "+ Add new" (opened in a new tab): the new Profile pages.
  shippingAddress: () => `${INF_BASE}/profile/addresses`,
  paymentMethods: () => `${INF_BASE}/profile/bank-accounts`,
  companyDetails: () => `${INF_BASE}/profile/company-details`,
  affiliateDeals: () => '/influencer/brand-deals',
  affiliateDeal: (trackierId: number | string) => `/influencer/brand-deals/campaigns/${encodeURIComponent(String(trackierId))}`,
};
